import React, { Component, ChangeEvent, RefObject } from "react";
import {
  Button,
  Checkbox,
  TextField,
  Chip,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Skeleton,
} from "@mui/material";
import { IFilterParams } from "ag-grid-community";
import { IFilterReactComp } from "ag-grid-react";
import styles from "./CheckboxFilter.module.scss";
import { AxiosInstance } from "axios";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";

import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList as List, ListChildComponentProps } from "react-window";
import { LoadingStatus } from "types/app";

interface CheckboxFilterParams {
  keyword: string;
  data: CheckboxItem[];
  filterActive: boolean;
  loadingStatus: LoadingStatus;
  storedData: string;
}

export interface CheckboxItem {
  checked: boolean;
  name: string;
  value: string;
}

class CheckboxFilter
  extends Component<IFilterParams, CheckboxFilterParams>
  implements IFilterReactComp
{
  private listRef: RefObject<List>;
  private title: string;

  constructor(props: IFilterParams) {
    super(props);
    this.state = {
      keyword: "",
      data: [],
      filterActive: false,
      loadingStatus: "notLoaded",
      storedData: "",
    };

    this.onButtonClick = this.onButtonClick.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.onKeywordChange = this.onKeywordChange.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.renderRow = this.renderRow.bind(this);
    this.handleClearKeyword = this.handleClearKeyword.bind(this);
    this.handleLabelClick = this.handleLabelClick.bind(this);
    this.listRef = React.createRef<List>();
    this.title = this.props.colDef.filterParams?.title ?? props.colDef.headerName;
  }

  componentDidMount() {
    const { queryParams, axios, key, nullOptionName, customPath, nameKey } =
      this.props.colDef.filterParams;
    if (!axios) {
      return;
    }
    (axios as AxiosInstance)
      .get<string[]>(customPath ?? `/api/portfolio/metadata/${queryParams}`, {})
      .then((res) => {
        const data = res.data;
        const displayData: CheckboxItem[] = [];
        data.forEach((i: any) => {
          const item = {
            checked: false,
            name: nameKey ? i[nameKey] : key ? i["name"] : i,
            value: key ? i[key] : i,
          };
          displayData.push(item);
        });
        if (nullOptionName) {
          displayData.unshift({
            checked: false,
            name: nullOptionName,
            value: customPath?.includes("category=SUITE_ANCHOR") ? "0" : "null",
          });
        }
        this.setState({
          data: displayData,
          loadingStatus: "loaded",
        });
      })
      .catch((error) => {
        console.log(error);
        this.setState({ loadingStatus: "failed" });
      });
  }

  isFilterActive() {
    return this.state.filterActive;
  }

  doesFilterPass() {
    return true;
  }

  afterGuiAttached() {
    const { storedData, loadingStatus, data } = this.state;
    if (storedData && loadingStatus === "loaded") {
      const valueArray = storedData.split("|");
      this.setState({
        data: data.map((x) =>
          valueArray.some((i) => i === x.value) ? { ...x, checked: true } : x
        ),
        storedData: "",
      });
    }
  }

  onItemChange(action: "add" | "remove" | "edit", item: CheckboxItem) {
    const { data } = this.state;
    if (action === "add") {
      this.setState({ data: [item, ...data] });
    } else if (action === "remove") {
      const removeItem = data.find((x) => x.value === item.value);
      this.setState({ data: data.filter((x) => x.value !== item.value) }, () => {
        if (removeItem?.checked) {
          this.props.filterChangedCallback();
        }
      });
    } else if (action === "edit") {
      const editItem = data.find((x) => x.value === item.value);
      this.setState(
        {
          data: data.map((x) =>
            x.value === item.value ? { ...x, name: item.name, value: item.name } : x
          ),
        },
        () => {
          this.listRef.current?.forceUpdate();
          if (editItem?.checked) {
            this.props.filterChangedCallback();
          }
        }
      );
    }
  }

  getModel() {
    if (this.state.storedData) {
      return {
        filterType: "checkboxFilter",
        type: "in",
        filter: this.state.storedData,
      };
    } else if (this.state.filterActive && this.state.loadingStatus === "loaded") {
      const checkboxFilterValue = this.state.data
        .filter((x) => x.checked)
        .reduce((prv, cur, i) => (i === 0 ? `${cur.value}` : `${prv}|${cur.value}`), "");
      return checkboxFilterValue.length
        ? {
            filterType: "checkboxFilter",
            type: "in",
            filter: checkboxFilterValue,
          }
        : null;
    } else {
      return null;
    }
  }

  setModel(model: any) {
    if (!!model) {
      const { filter } = model;
      if (filter) {
        if (this.state.loadingStatus === "loaded") {
          const valueArray = (filter as string).split("|");
          const tempData = this.state.data.map((i) =>
            valueArray.some((x) => x === i.value)
              ? { ...i, checked: true }
              : { ...i, checked: false }
          );
          this.setState(
            {
              data: tempData,
              filterActive: true,
            },
            () => {
              this.props.filterChangedCallback();
              this.listRef.current?.forceUpdate();
            }
          );
        } else {
          this.setState({ storedData: filter, filterActive: true }, () => {
            this.props.filterChangedCallback();
            this.listRef.current?.forceUpdate();
          });
        }
      }
    } else {
      this.setState(
        {
          filterActive: false,
          storedData: "",
          keyword: "",
          data: this.state.data.map((i) => ({
            ...i,
            checked: false,
          })),
        },
        () => {
          this.props.filterChangedCallback();
          this.listRef.current?.forceUpdate();
        }
      );
    }
  }

  onButtonClick(type: "clear" | "apply") {
    switch (type) {
      case "clear":
        return () => {
          const tempData = this.state.data.map((item) => ({
            ...item,
            checked: false,
          }));
          this.setState(
            {
              data: tempData,
              keyword: "",
              filterActive: false,
            },
            () => {
              this.listRef.current?.forceUpdate();
              this.props.filterChangedCallback();
            }
          );
        };
      case "apply":
        return () => {
          this.setState({ filterActive: true }, () => {
            this.props.filterChangedCallback();
          });
        };
    }
  }

  handleChange(e: ChangeEvent<HTMLInputElement>) {
    const tempData = this.state.data.map((i) =>
      i.name === e.target.name ? { ...i, checked: e.target.checked } : i
    );
    this.setState({ data: tempData }, () => {
      this.listRef.current?.forceUpdate();
    });
  }

  onKeywordChange(e: ChangeEvent<HTMLInputElement>) {
    this.setState({ keyword: e.target.value });
  }

  handleDelete = (item: CheckboxItem) => () => {
    const tempData = this.state.data.map((i) =>
      i.name === item.name ? { ...i, checked: false } : i
    );
    this.setState({ data: tempData }, () => {
      this.listRef.current?.forceUpdate();
    });
  };

  handleLabelClick = (item: CheckboxItem) => () => {
    const tempData = this.state.data.map((i) =>
      i.value === item.value ? { ...i, checked: !item.checked } : i
    );
    this.setState({ data: tempData }, () => {
      this.listRef.current?.forceUpdate();
    });
  };

  handleClearKeyword = () => {
    this.setState({ keyword: "" });
  };

  renderRow = ({ index, style, data }: ListChildComponentProps) => {
    const item = data[index];
    return (
      <ListItem
        style={{ padding: 0, ...style }}
        key={item.value}
      >
        <ListItemIcon>
          <Checkbox
            checked={item.checked}
            name={item.name}
            onChange={this.handleChange}
            color="primary"
            classes={{
              colorPrimary: styles.checkboxColorPrimary,
            }}
          />
        </ListItemIcon>
        <ListItemText
          primary={item.name}
          onClick={this.handleLabelClick(item)}
          style={{ cursor: "pointer" }}
        />
      </ListItem>
    );
  };

  render() {
    const { data, keyword, loadingStatus, filterActive } = this.state;
    const filteredData = data.filter((x) => x.name?.toLowerCase().includes(keyword.toLowerCase()));
    const checked = data.filter((x) => x.checked);

    if (loadingStatus === "failed")
      return (
        <div className={styles.failLoad}>
          <span>Sorry, failed to load data</span>
        </div>
      );

    return (
      <div>
        <div className={styles.filterWrapper}>
          <div className={styles.chipWrapper}>
            {checked.slice(0, 10).map((item) => (
              <Chip
                label={item.name}
                onDelete={this.handleDelete(item)}
                key={item.name}
                size="small"
                color="primary"
                classes={{
                  root: styles.chipRoot,
                  label: styles.chipLabel,
                }}
              />
            ))}
            {checked.length > 10 ? <div>{`${checked.length - 10} more`}</div> : null}
          </div>

          <TextField
            fullWidth
            placeholder="Enter Search Criteria..."
            margin="dense"
            variant="standard"
            autoFocus
            value={keyword}
            onChange={this.onKeywordChange}
            InputProps={{
              endAdornment: keyword ? (
                <IconButton
                  onClick={this.handleClearKeyword}
                  size="small"
                >
                  <HighlightOffOutlinedIcon
                    color="primary"
                    fontSize="small"
                  />
                </IconButton>
              ) : null,
            }}
          />

          <div style={{ textAlign: "center" }}>
            {loadingStatus === "loaded" ? (
              <div style={{ height: 200 }}>
                <AutoSizer>
                  {({ width, height }) => (
                    <List
                      ref={this.listRef}
                      height={height}
                      width={width}
                      itemCount={filteredData.length}
                      itemSize={42}
                      itemData={filteredData}
                    >
                      {this.renderRow}
                    </List>
                  )}
                </AutoSizer>
              </div>
            ) : (
              <>
                {Array.from({ length: 4 }).map((_, i) => (
                  <Skeleton
                    key={i}
                    width={160}
                    height={28}
                    variant="rectangular"
                    style={{ margin: "6px 12px" }}
                  />
                ))}
              </>
            )}
          </div>
        </div>
        <Divider />
        <div className="ag-filter-apply-panel">
          <Button
            variant="text"
            disabled={!filterActive}
            onClick={this.onButtonClick("clear")}
            className="ag-standard-button ag-filter-apply-panel-button"
          >
            Reset filter
          </Button>
          <Button
            disabled={!data.find((i) => i.checked)}
            variant="text"
            onClick={this.onButtonClick("apply")}
            className="ag-standard-button ag-filter-apply-panel-button"
          >
            Apply filter
          </Button>
        </div>
      </div>
    );
  }
}

export default CheckboxFilter;
